import { useMemo } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "next-i18next";
import { useGenericActivityFromElysiumMutation } from "src/api/client/generic-activity-from-elysium.api";
import { EnumActivityType } from "src/api/client/generic-activity.api";
import { IFiguresPickerData, IRankingExtendedSearch } from "src/api/server/extended-search.types";
import { ITableFigure, ITableFigureSection } from "src/api/server/watchlists/watchlists.types";
import { IconSettingsGear } from "src/components/icons-mui/settings-gear";
import { Button } from "src/components-mui/base/button/button";
import { AdjustFiguresPreferencesModal } from "src/components-mui/common/modal/adjust-figures-preferences-modal/adjust-figures-preferences-modal";
import { Container } from "src/components-mui/layout/container";
import { useUserStore } from "src/stores/user-store";
import { DownloadButton } from "./components/download-button/download-button";
import { RankingsDropdownSelector } from "./components/rankings-dropdown-selector/rankings-dropdown-selector";
import { getVisibleRankings } from "./components/rankings-dropdown-selector/utils/get-visible-rankings";
import { SortDirectionButton } from "./components/sort-direction-button/sort-direction-button";
import { ISelectedRanking } from "./components/types";
import { ViewToggleButton } from "./components/view-toggle-button/view-toggle-button";
import { handleSortDirection, handleSortingChange, setSelectedFigures as setSelectedFigureIdsInStore, setView } from "./store/actions";
import { ViewMode, useSearchStore } from "./store/search-store";
import { getNextAvailableRanking } from "./utils/get-next-available-ranking";

interface ISortSectionProps {
    resultCount: number | null;
    rankings: ReadonlyArray<IRankingExtendedSearch>;
    relevanceRanking: IRankingExtendedSearch;
    userNumOfDownloads: number;
    figuresPicker: IFiguresPickerData;
}

const DEFAULT_SELECTED_RANKING: ISelectedRanking = { id: null, sortDirection: null };

export const SortSection = ({ resultCount, rankings, relevanceRanking, userNumOfDownloads, figuresPicker }: ISortSectionProps) => {
    const { t } = useTranslation("extended-search");
    const { sortDirection, selectedRanking, view, selectedFigures: selectedFigureIds } = useSearchStore();
    const { isLoggedIn } = useUserStore();
    const genericActivity = useGenericActivityFromElysiumMutation();

    const hasResults = resultCount !== null;
    const isDefaultRankingSelected = selectedRanking?.id === null;

    const visibleRankings = useMemo(
        () => getVisibleRankings(rankings, relevanceRanking, selectedFigureIds),
        [rankings, relevanceRanking, selectedFigureIds]
    );

    const selectedRankingLabel = useMemo(() => {
        const selectedRankingData = visibleRankings.find(ranking => ranking.id === selectedRanking?.id) ?? visibleRankings[0];
        return selectedRankingData.label;
    }, [visibleRankings, selectedRanking]);

    const figureSections: ITableFigureSection[] = (figuresPicker?.figureGroups ?? []).map(group => ({
        label: group.label,
        data: group.data as ITableFigure[],
    }));

    const allFigures = useMemo(() => figuresPicker.figureGroups.flatMap(group => group.data as ITableFigure[]), [figuresPicker]);

    const selectedFigures = useMemo(
        () =>
            selectedFigureIds
                .map(figureId => allFigures.find(figure => figure.id === figureId))
                .filter((figure): figure is ITableFigure => Boolean(figure)),
        [allFigures, selectedFigureIds]
    );

    const defaultFigureIds = useMemo(() => figuresPicker.defaultFigures.map(figure => figure.id), [figuresPicker]);

    const onApplySelectedFigures = (figures: ITableFigure[]) => {
        const newFigureIds = figures.map(figure => figure.id);
        const hadSortableFigureBefore = visibleRankings.some(ranking => ranking.id !== null && ranking.id !== relevanceRanking.id);

        setSelectedFigureIdsInStore(newFigureIds);

        if (JSON.stringify(newFigureIds) !== JSON.stringify(defaultFigureIds)) {
            void genericActivity({
                activityTypeId: EnumActivityType.SearchCustomFiguresSelected,
                parameter: newFigureIds.join(","),
                referrer: window.location.href,
            });
        }

        const removedRankingId = selectedRanking?.id ?? null;
        const isSelectedRankingRemoved = removedRankingId !== null && !newFigureIds.includes(removedRankingId);
        const shouldSelectNewlyAddedSortableFigure = isDefaultRankingSelected && !hadSortableFigureBefore;

        if (!isSelectedRankingRemoved && !shouldSelectNewlyAddedSortableFigure) {
            return;
        }

        const { id, sortDirection: direction } = getNextAvailableRanking({
            rankings,
            relevanceRanking,
            selectedFigures: newFigureIds,
        });
        handleSortingChange({ sortByRankingId: id, sortDirection: direction });
    };

    const onClickSortDirection = () => {
        handleSortDirection();
    };

    const onSortingChange = ({ id, sortDirection: direction }: ISelectedRanking) => {
        handleSortingChange({ sortByRankingId: id, sortDirection: direction });
    };

    return (
        <Container component="section" sx={{ pt: { xs: 4, sm: 5 }, pb: { xs: 2, sm: 3 } }}>
            <AdjustFiguresPreferencesModal
                selectedFigures={selectedFigures}
                figureSections={figureSections}
                onApply={onApplySelectedFigures}
                maxSelectable={5}
                translations={{
                    title: t("adjust-figures-preferences-modal.title"),
                    descriptionOrdering: t("adjust-figures-preferences-modal.description-ordering"),
                    descriptionSelecting: t("adjust-figures-preferences-modal.description-selecting"),
                }}
            >
                {({ onClick: onAdjustFigures }) => (
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: "center", gap: 2 }}>
                        {view !== ViewMode.Table && (
                            <Box
                                role="group"
                                aria-label={t("labels.sort-controls")}
                                sx={{
                                    display: "flex",
                                    gap: 2,
                                    alignItems: { xs: "flex-end", sm: "center" },
                                    width: "100%",
                                }}
                            >
                                <Box sx={{ width: { xs: "100%", sm: "250px" }, flexGrow: { xs: 1, sm: 0 } }}>
                                    <RankingsDropdownSelector
                                        rankings={rankings}
                                        relevanceRanking={relevanceRanking}
                                        selectedRanking={selectedRanking ?? DEFAULT_SELECTED_RANKING}
                                        selectedFiguresIds={selectedFigureIds}
                                        isDisabled={!hasResults}
                                        onChange={onSortingChange}
                                        onAdjustFigures={onAdjustFigures}
                                    />
                                </Box>

                                <SortDirectionButton
                                    onClick={onClickSortDirection}
                                    isDisabled={!hasResults || isDefaultRankingSelected}
                                    sortDirection={sortDirection}
                                    selectedRankingLabel={selectedRankingLabel}
                                />
                            </Box>
                        )}
                        <Box sx={{ display: "flex", gap: 2, alignItems: "center", ml: "auto", width: { xs: "100%", sm: "auto" } }}>
                            {isLoggedIn && <DownloadButton userNumOfDownloads={userNumOfDownloads} />}

                            <Box sx={{ flexGrow: { xs: 1, sm: 0 } }}>
                                <Button
                                    variant="outlined"
                                    color="gray"
                                    size="small"
                                    onClick={onAdjustFigures}
                                    sx={{ width: { xs: "100%", sm: "auto" }, justifyContent: "center" }}
                                    startIcon={<IconSettingsGear sx={{ fontSize: "18px", mr: 1 }} />}
                                >
                                    <Box component="span" sx={{ display: { xs: "none", sm: "none", md: "inline" } }}>
                                        {t("buttons.adjust-key-figures")}
                                    </Box>
                                    <Box component="span" sx={{ display: { xs: "inline", sm: "inline", md: "none" } }}>
                                        {t("key-figures")}
                                    </Box>
                                </Button>
                            </Box>
                            <ViewToggleButton viewMode={view} onChange={setView} isDisabled={!hasResults} />
                        </Box>
                    </Box>
                )}
            </AdjustFiguresPreferencesModal>
        </Container>
    );
};
